import { AbsoluteCenter, Center } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import RegularSolidButton from '../../components/buttons/RegularSolidButton';
import { MBTICard } from '../../components/fan-buddy/MBTICard';
import StickyHeader from '../../components/StickyHeader';
import { FAN_BUDDY_MBTI, getCompatibleMBTI, getIncompatibleMBTI } from '../../utils/FanBuddyHelper';
import { isDarkMode } from '../../utils/ThemeHelper';
import { t } from 'i18next';
import { Xmark } from '@peacecover/icon';

const ShowFanBuddyCardPage = () => {
  const navigate = useNavigate();

  const currentQueryParams = new URLSearchParams(window.location.search);
  const mbti = currentQueryParams.get('mbti') ?? '';
  const showFindButton = currentQueryParams.get('showFindButton') ?? false;
  const returnToFeed = currentQueryParams.get('returnToFeed') ?? false;

  const onClickReStart = () => {
    navigate('/fan-buddy/intro');
  };

  const exit = () => {
    if (returnToFeed) {
      navigate('/home', { replace: true });
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#222222]">
      <StickyHeader
        showBackButton={false}
        transparent={true}
        leftContent={
          <Xmark className="text-pure-black dark:text-pure-white" size={20} onClick={exit} />
        }
      />
      <AbsoluteCenter
        style={{
          marginTop: 'calc(24px + env(safe-area-inset-top))',
          padding: '0 24px',
          width: '100%',
          maxWidth: 'calc(var(--chakra-sizes-xl))'
        }}
      >
        <Center flexDirection={'column'}>
          <h1 className="text-[26px] font-bold leading-[31px] text-center text-text-main-lv1 whitespace-pre-wrap">
            {showFindButton
              ? t('ShowFanBuddyCard.completeTitle')
              : t('ShowFanBuddyCard.myFanBuddyCard')}
          </h1>
          <p className="w-full text-[15px] font-normal leading-[18px] text-center pt-[14px] mb-[16px] text-text-main-lv4">
            {t('ShowFanBuddyCard.description')}
          </p>
          <MBTICardContainer>
            <MBTICard mbti={mbti} isDarkMode={isDarkMode()} />
          </MBTICardContainer>
          <div className="mt-[16px] border-[2px] rounded-[12px] w-full text-left py-[12px] px-[18px] border-separator-light-1 dark:border-separator-dark-1">
            <div className="text-[13px] font-semibold leading-[22px] text-main-light-3 dark:text-main-dark-3 mb-[1px]">
              {t('ShowFanBuddyCard.likeYourMBTI', {
                mbti: FAN_BUDDY_MBTI[mbti as keyof typeof FAN_BUDDY_MBTI].name
              })}
            </div>
            {FAN_BUDDY_MBTI[mbti as keyof typeof FAN_BUDDY_MBTI]['descriptions']?.map(
              (description: string, index: number) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: this is a key for the description
                  key={index}
                  className="flex flex-row gap-[6px] items-center"
                  style={{ lineHeight: '20px' }}
                >
                  <span className="text-[#666666] dark:text-[#AAABAC]">•</span>
                  <div className="text-[13px] font-normal leading-[16px] text-main-light-3 dark:text-main-dark-3">
                    {description}
                  </div>
                </div>
              )
            )}
          </div>
          <div className="flex justify-between w-full mt-[16px]">
            <div className="flex-1 text-center">
              <p className="text-[12px] font-medium leading-[15px] text-center pb-[10px]">
                {t('ShowFanBuddyCard.goodMatch')}
              </p>
              <div className="flex flex-col gap-[3px]">
                {getCompatibleMBTI(mbti).map((compatibleMBTI: keyof typeof FAN_BUDDY_MBTI) => (
                  <CompatibleMBTIName
                    key={compatibleMBTI}
                    textPrimaryColor={FAN_BUDDY_MBTI[compatibleMBTI]['textColor']['primary']}
                    textSecondaryColor={FAN_BUDDY_MBTI[compatibleMBTI]['textColor']['secondary']}
                  >
                    {FAN_BUDDY_MBTI[compatibleMBTI]['name']}
                  </CompatibleMBTIName>
                ))}
              </div>
            </div>
            {/* 세로보더선 */}
            <div className="flex-1 text-center relative h-full">
              <div className="absolute left-0 top-0 w-[1px] h-full bg-separator2 dark:bg-separator1" />
              <p className="text-[12px] font-medium leading-[15px] text-center pb-[10px]">
                {t('ShowFanBuddyCard.badMatch')}
              </p>
              <div className="flex flex-col gap-[3px]">
                {getIncompatibleMBTI(mbti).map((incompatibleMBTI: keyof typeof FAN_BUDDY_MBTI) => (
                  <CompatibleMBTIName
                    key={incompatibleMBTI}
                    textPrimaryColor={FAN_BUDDY_MBTI[incompatibleMBTI]['textColor']['primary']}
                    textSecondaryColor={FAN_BUDDY_MBTI[incompatibleMBTI]['textColor']['secondary']}
                  >
                    {FAN_BUDDY_MBTI[incompatibleMBTI]['name']}
                  </CompatibleMBTIName>
                ))}
              </div>
            </div>
          </div>
          {showFindButton && (
            <RegularSolidButton
              text={t('ShowFanBuddyCard.startButton')}
              style={{
                marginTop: '24px',
                padding: '16px 20px',
                width: '100%',
                borderRadius: '8px'
              }}
              onClicked={() => navigate('/fan-buddy/find')}
            />
          )}
          <button
            type="button"
            className="
            text-[13px] font-normal leading-[16px] text-center underline
            text-text-main-lv3 mt-[24px] safeAreaMarginBottom
            "
            onClick={() => onClickReStart()}
          >
            {t('ShowFanBuddyCard.restartButton')}
          </button>
        </Center>
      </AbsoluteCenter>
    </div>
  );
};

export default ShowFanBuddyCardPage;

const CompatibleMBTIName = ({
  textPrimaryColor,
  textSecondaryColor,
  children
}: {
  textPrimaryColor: string;
  textSecondaryColor: string;
  children: React.ReactNode;
}) => {
  return (
    <div
      className="text-[13px] font-bold leading-[16px] text-center bg-clip-text text-transparent"
      style={{
        background: `linear-gradient(90deg, rgba(${textPrimaryColor},1) 0%, rgba(${textSecondaryColor},1) 100%)`,
        WebkitBackgroundClip: 'text'
      }}
    >
      {children}
    </div>
  );
};

const MBTICardContainer = ({
  children,
  className = '',
  style = {}
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) => {
  const dark = isDarkMode();
  const overlayBackground = dark
    ? 'linear-gradient(135deg, rgba(203,227,255,0) 0%, rgba(0,0,0,0.7) 60%, rgba(151,255,198,0.64) 100%), linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,1) 6%, rgba(0,0,0,0.7) 20%, rgba(0,0,0,1) 36%, rgba(0,0,0,0.48) 70%, rgba(0,0,0,1) 100%)'
    : 'linear-gradient(135deg, rgba(203,227,255,0) 0%, rgba(255,255,255,0.7) 60%, rgba(151,255,198,0.64) 100%), linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,1) 6%, rgba(255,255,255,0.3) 20%, rgba(255,255,255,1) 36%, rgba(255,255,255,0.48) 70%, rgba(255,255,255,1) 100%)';

  return (
    <div
      className={`mt-[20px] mb-[12px] flex justify-center items-center relative ${className ? ` ${className}` : ''}`}
      style={style}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-[-10px] rounded-[24px] z-0 opacity-90 blur-[28px] mix-blend-lighten"
        style={{ background: overlayBackground }}
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
};
